// ToastService.js
// A reactive service that allows showing toast messages from anywhere in the application

import { reactive } from 'vue';

// Create a reactive state object to store toast data
const state = reactive({
  toasts: [], // Array of toast objects
  toastIdCounter: 0 // Counter to generate unique IDs for toasts
});

// Toast types (used for styling)
export const ToastType = {
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info',
  WARNING: 'warning'
};

// Default toast options
const defaultOptions = {
  type: ToastType.INFO,
  autohide: true,
  delay: 5000, // 5 seconds
  position: 'top-end' // top-right corner
};

/**
 * Show a toast notification
 * @param {string} message - The message to display
 * @param {Object} options - Toast configuration options
 * @returns {number} The ID of the toast (can be used to dismiss it)
 */
function showToast(message, options = {}) {
  // Merge default options with provided options
  const toastOptions = { ...defaultOptions, ...options };
  
  // Create a new toast with a unique ID
  const id = state.toastIdCounter++;
  const toast = {
    id,
    message,
    ...toastOptions,
    show: true,
    created: new Date()
  };
  
  // Add the toast to the array
  state.toasts.push(toast);
  
  // Auto-hide the toast after the specified delay
  if (toastOptions.autohide) {
    setTimeout(() => {
      dismissToast(id);
    }, toastOptions.delay);
  }
  
  return id;
}

/**
 * Dismiss a specific toast by ID
 * @param {number} id - The ID of the toast to dismiss
 */
function dismissToast(id) {
  const index = state.toasts.findIndex(t => t.id === id);
  if (index !== -1) {
    state.toasts[index].show = false;
    // Remove the toast after animation completes
    setTimeout(() => {
      const removeIndex = state.toasts.findIndex(t => t.id === id);
      if (removeIndex !== -1) {
        state.toasts.splice(removeIndex, 1);
      }
    }, 500); // Animation time
  }
}

/**
 * Dismiss all toasts
 */
function dismissAllToasts() {
  state.toasts.forEach(toast => {
    dismissToast(toast.id);
  });
}

/**
 * Show a success toast notification
 * @param {string} message - The message to display
 * @param {Object} options - Additional options
 * @returns {number} The ID of the toast
 */
function success(message, options = {}) {
  return showToast(message, { ...options, type: ToastType.SUCCESS });
}

/**
 * Show an error toast notification
 * @param {string} message - The message to display
 * @param {Object} options - Additional options
 * @returns {number} The ID of the toast
 */
function error(message, options = {}) {
  return showToast(message, { ...options, type: ToastType.ERROR });
}

/**
 * Show an info toast notification
 * @param {string} message - The message to display
 * @param {Object} options - Additional options
 * @returns {number} The ID of the toast
 */
function info(message, options = {}) {
  return showToast(message, { ...options, type: ToastType.INFO });
}

/**
 * Show a warning toast notification
 * @param {string} message - The message to display
 * @param {Object} options - Additional options
 * @returns {number} The ID of the toast
 */
function warning(message, options = {}) {
  return showToast(message, { ...options, type: ToastType.WARNING });
}

// Export the service methods and state
export const ToastService = {
  // Methods
  showToast,
  dismissToast,
  dismissAllToasts,
  success,
  error,
  info,
  warning,
  
  // Reactive state
  state
};

export default ToastService;
